import { useGeneratePath, useStaticUrls } from 'platform-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { getCurrentClass } from 'common.components/user/selectors';
import { calendarPlanActions } from 'store/calendar-plan/actions';
import { useCalendarPlanQuery } from 'store/calendar-plan/queries';
import { getThemeActivities } from 'store/calendar-plan/selectors';
import { getPeriodLabel } from 'utils/get-period-label';

import { useCurrentGrade } from '../../utils/use-current-grade';
import { CalendarPlanShowcaseAnimatedView } from '../calendar-plan-showcase/__animated-view/calendar-plan-showcase__animated-view';
import { CalendarPlanShowcase } from '../calendar-plan-showcase/calendar-plan-showcase';
import { CalendarPlanButton } from '../library.calendar-plan-showcase/__calendar-plan-button/calendar-plan-showcase__calendar-plan-button';
import { CalendarPlanShowcaseHeader } from '../library.calendar-plan-showcase/__header/calendar-plan-showcase__header';
import { MetrikaClickFromParam, useMetrika } from '../library.calendar-plan-showcase/hooks/useMetrika';
import { CalendarPlanThemeList } from '../library.calendar-plan.new/__theme-list/calendar-plan__theme-list';
import { CalendarPlanSkeleton } from '../library.calendar-plan.new/_skeleton-view/calendar-plan_skeleton-view';
import { useLessonQuery } from '../library.calendar-plan.new/queries';
import { getNextTheme, getPrevTheme } from '../library.calendar-plan.new/utils';

import { CalendarPlanThemeContent } from './__content/calendar-plan-theme__content';
import { getLessonActivity } from './utils';

// eslint-disable-next-line complexity
export const CalendarPlanTheme = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const staticUrls = useStaticUrls();
    const history = useHistory();
    const location = useLocation();
    const getRouterPath = useGeneratePath();
    const params = useParams<Record<string, string>>();
    const { subject, classId, calendarPlanThemeId } = params;

    const windowScrollYContainerRef = useRef(0);
    const showcaseRef = useRef<HTMLDivElement>(null);

    const [isCalendarPlanOpened, setCalendarPlanOpened] = useState(false);
    const currentTeam = useSelector((store) => getCurrentClass(store, { classId }));
    const grade = useCurrentGrade();
    const calendarPlanQuery = useCalendarPlanQuery(subject, grade);
    const calendarPlan = calendarPlanQuery.data;
    const calendarPlanThemes = calendarPlan?.themes_with_periods || [];
    const calendarPlanThemeActivities = useSelector(getThemeActivities);
    const currentThemeLink = calendarPlanThemes.find((item) => item.theme.id === calendarPlanThemeId) || null;
    const calendarPlanTheme = calendarPlanThemeActivities[calendarPlanThemeId] || null;
    const prevThemeLink = getPrevTheme(currentThemeLink, calendarPlanThemes);
    const nextThemeLink = getNextTheme(currentThemeLink, calendarPlanThemes);
    const nextCalendarPlanTheme = nextThemeLink ? calendarPlanThemeActivities[nextThemeLink.theme.id] : null;

    const lessonActivity = getLessonActivity(calendarPlanTheme?.activities);
    const lessonQuery = useLessonQuery(lessonActivity?.content_id);

    const nextThemeLessonActivity = getLessonActivity(nextCalendarPlanTheme?.activities);
    const nextLessonQuery = useLessonQuery(nextThemeLessonActivity?.content_id);

    const shouldShowShowcaseSkeleton =
        !calendarPlanTheme || (nextThemeLink && !nextCalendarPlanTheme) || lessonQuery.isLoading;

    const themeStartDate = currentThemeLink?.period.start_date ? new Date(currentThemeLink?.period.start_date) : null;
    const {
        reachCalendarPlanButtonClickGoal,
        reachNextLessonClickGoal,
        reachPrevLessonClickGoal,
        reachUploadCalendarButtonClick,
    } = useMetrika(themeStartDate);

    const periodLabel = currentThemeLink
        ? getPeriodLabel(currentThemeLink.period.start_date, currentThemeLink.period.end_date).toLocaleUpperCase()
        : '';
    const subtitle = isCalendarPlanOpened ? intl.formatMessage({ id: 'calendarPlan.grade' }, { grade }) : periodLabel;
    const description = isCalendarPlanOpened ? calendarPlan?.description : null;

    const handleNextLessonAnimationEntering = () => {
        if (isCalendarPlanOpened && showcaseRef.current) {
            const { top: showcaseTop } = showcaseRef.current.getBoundingClientRect();

            const isShowcaseTopOutOfViewport = showcaseTop < 0;

            if (isShowcaseTopOutOfViewport) {
                const windowScrollTop = window.pageYOffset;

                window.scrollTo({
                    top: windowScrollTop + showcaseTop - 100,
                });
            }

            return;
        }

        if (windowScrollYContainerRef?.current) {
            window.scrollTo({ top: windowScrollYContainerRef.current });
        }
    };

    const handleShowCalendarPlanButtonClick = useCallback(() => {
        // Сохраняем позицию скролла перед анимацией
        // и после старта анимации восстанавливаем обратно,
        // так как react-transition-group ломает её,
        // если верхняя граница анимируемого контейнера не помещается в экран
        windowScrollYContainerRef.current = window.scrollY;

        if (!isCalendarPlanOpened) {
            setCalendarPlanOpened(true);
            reachCalendarPlanButtonClickGoal(currentThemeLink?.theme.id);
        } else {
            setCalendarPlanOpened(false);
        }

        setCalendarPlanOpened(!isCalendarPlanOpened);
    }, [currentThemeLink?.theme.id, isCalendarPlanOpened, reachCalendarPlanButtonClickGoal]);

    const handleUploadCalendarPlan = () => {
        const uploadCalendarUrl = intl.formatMessage(
            { id: 'calendarPlan.uploadUrl', defaultMessage: '' },
            { teamId: currentTeam?.id, subject }
        );

        if (uploadCalendarUrl) {
            reachUploadCalendarButtonClick(MetrikaClickFromParam.CALENDAR_PLAN);
            window.open(uploadCalendarUrl, '_blank');
        }
    };

    const redirectToTheme = (themeId: string) => {
        const url = classId
            ? staticUrls.labClassLibrarySubjectCalendarPlanTheme
            : staticUrls.labLibrarySubjectCalendarPlanTheme;

        history.push(`${getRouterPath(url, { calendarPlanThemeId: themeId })}${location.search}`);
    };

    const handleNextItemClick = () => {
        const nextThemeId = getNextTheme(currentThemeLink, calendarPlanThemes)?.theme.id;
        if (nextThemeId) {
            redirectToTheme(nextThemeId);
            reachNextLessonClickGoal(nextThemeId);
        }
    };

    const handlePrevItemClick = () => {
        const prevThemeId = getPrevTheme(currentThemeLink, calendarPlanThemes)?.theme.id;
        if (prevThemeId) {
            redirectToTheme(prevThemeId);
            reachPrevLessonClickGoal(prevThemeId);
        }
    };

    const handleThemeClick = (themeId: string) => {
        if (themeId === params.calendarPlanThemeId) {
            setCalendarPlanOpened(false);
        }
    };

    useEffect(() => {
        setCalendarPlanOpened(false);
    }, [calendarPlanThemeId]);

    useEffect(() => {
        if (!calendarPlanThemes.length) {
            return;
        }

        const themesToLoad: string[] = [];
        if (!calendarPlanTheme) {
            themesToLoad.push(calendarPlanThemeId);
        }

        if (nextThemeLink) {
            if (!nextCalendarPlanTheme) {
                themesToLoad.push(nextThemeLink.theme.id);
            }

            const linkId = getNextTheme(nextThemeLink, calendarPlanThemes)?.theme.id;
            if (linkId && !calendarPlanThemeActivities[linkId]) {
                themesToLoad.push(linkId);
            }
        }

        if (themesToLoad.some((themeId) => !calendarPlanThemes.some(({ theme }) => theme.id === themeId))) {
            return;
        }

        dispatch(
            calendarPlanActions.getThemeActivitiesRequest({
                themeIds: themesToLoad,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calendarPlan, calendarPlanThemeId]);

    useEffect(() => {
        if (calendarPlanQuery.isSuccess && !calendarPlanThemes.some(({ theme }) => theme.id === calendarPlanThemeId)) {
            const path = classId
                ? staticUrls.labClassLibrarySubjectCalendarPlan
                : staticUrls.labLibrarySubjectCalendarPlan;
            history.replace(`${getRouterPath(path)}${location.search}`);
        }
        // eslint-disable-next-line
    }, [calendarPlan, calendarPlanThemeId]);

    if (!calendarPlan || !currentThemeLink) {
        return <CalendarPlanSkeleton />;
    }

    const renderCalendarPlanContent = () => {
        return isCalendarPlanOpened ? (
            <CalendarPlanThemeList
                calendarPlan={calendarPlan}
                onThemeClick={handleThemeClick}
                onUploadCalendarPlan={handleUploadCalendarPlan}
            />
        ) : (
            <CalendarPlanThemeContent
                currentTheme={{
                    ...currentThemeLink,
                    activities: calendarPlanTheme.activities,
                }}
                currentThemeLesson={lessonQuery?.data}
                hasPrevItem={Boolean(prevThemeLink?.theme.id && !prevThemeLink.is_disabled)}
                nextTheme={
                    nextThemeLink && !nextThemeLink.is_disabled
                        ? {
                              ...nextThemeLink,
                              activities: nextCalendarPlanTheme?.activities || [],
                          }
                        : undefined
                }
                nextThemeLesson={nextLessonQuery?.data}
                onNextItemClick={handleNextItemClick}
                onPrevItemClick={handlePrevItemClick}
                onTeaserOpenPlanButtonClick={handleShowCalendarPlanButtonClick}
            />
        );
    };

    return (
        <CalendarPlanShowcase innerRef={showcaseRef} mix="library-calendar-plan-showcase">
            <CalendarPlanShowcaseAnimatedView
                animationKey={`${currentThemeLink.id},${isCalendarPlanOpened}`}
                onAnimationEntering={handleNextLessonAnimationEntering}
            >
                <CalendarPlanShowcaseHeader
                    button={
                        <CalendarPlanButton
                            isCalendarPlanOpened={isCalendarPlanOpened}
                            onClick={handleShowCalendarPlanButtonClick}
                        />
                    }
                    description={description || ''}
                    handleUploadButtonClick={handleUploadCalendarPlan}
                    isOpened={isCalendarPlanOpened}
                    subtitle={subtitle}
                />
                {shouldShowShowcaseSkeleton ? (
                    <CalendarPlanSkeleton
                        hasNextItem={Boolean(nextThemeLink && !nextThemeLink.is_disabled)}
                        hasPrevItem={Boolean(prevThemeLink?.theme.id && !prevThemeLink.is_disabled)}
                        onNextItemClick={handleNextItemClick}
                        onPrevItemClick={handlePrevItemClick}
                        withHeader={false}
                    />
                ) : (
                    renderCalendarPlanContent()
                )}
            </CalendarPlanShowcaseAnimatedView>
        </CalendarPlanShowcase>
    );
};
